import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Calculator as CalcIcon,
  TrendingUp,
  LineChart,
  TrendingDown,
  Info,
  Bitcoin,
} from "lucide-react";

const Calculators = () => {
  // Tax Calculator State
  const [taxForm, setTaxForm] = useState({
    income: "",
    deductions: "",
    regime: "new",
  });
  const [taxResult, setTaxResult] = useState(null);

  // ROI Calculator State
  const [roiForm, setRoiForm] = useState({
    investment_amount: "",
    expected_return: "",
    time_period: "",
  });
  const [roiResult, setRoiResult] = useState(null);

  // Market Data State
  const [marketTab, setMarketTab] = useState("stocks");
  const [cryptoData, setCryptoData] = useState([]);
  const [cryptoLoading, setCryptoLoading] = useState(false);
  const [stocks, setStocks] = useState([
    {
      symbol: "RELIANCE.NS",
      name: "Reliance Industries",
      price: 2950.5,
      change: 12.5,
      percent: 0.42,
    },
    {
      symbol: "TCS.NS",
      name: "Tata Consultancy",
      price: 3845.2,
      change: -15.4,
      percent: -0.4,
    },
    {
      symbol: "HDFCBANK.NS",
      name: "HDFC Bank",
      price: 1440.8,
      change: 8.2,
      percent: 0.57,
    },
    {
      symbol: "INFY.NS",
      name: "Infosys",
      price: 1680.0,
      change: 15.6,
      percent: 0.93,
    },
    {
      symbol: "SBIN.NS",
      name: "State Bank of India",
      price: 765.3,
      change: -2.1,
      percent: -0.27,
    },
    {
      symbol: "BHARTIARTL.NS",
      name: "Bharti Airtel",
      price: 1210.45,
      change: 5.8,
      percent: 0.48,
    },
  ]);

  // Real API Fetch for Crypto (CoinGecko allows free frontend CORS requests)
  useEffect(() => {
    const fetchCrypto = async () => {
      setCryptoLoading(true);
      try {
        const response = await fetch(
          "https://api.coingecko.com/api/v3/coins/markets?vs_currency=inr&ids=bitcoin,ethereum,binancecoin,solana,ripple,cardano&order=market_cap_desc&per_page=6&page=1&sparkline=false",
        );
        const data = await response.json();
        setCryptoData(data);
      } catch (error) {
        console.error("Failed to fetch crypto data", error);
      } finally {
        setCryptoLoading(false);
      }
    };
    fetchCrypto();
    // Refresh every 60 seconds
    const interval = setInterval(fetchCrypto, 60000);
    return () => clearInterval(interval);
  }, []);

  // Simulate live Indian market fluctuations every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setStocks((prevStocks) =>
        prevStocks.map((stock) => {
          const fluctuation = Math.random() * 6 - 3;
          const newPrice = Math.max(0, stock.price + fluctuation);
          const newChange = stock.change + fluctuation;
          const newPercent = (newChange / (newPrice - newChange)) * 100;
          return {
            ...stock,
            price: newPrice,
            change: newChange,
            percent: newPercent,
          };
        }),
      );
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // --- ADVANCED TAX CALCULATION LOGIC ---
  const calculateTax = () => {
    const income = parseFloat(taxForm.income) || 0;
    const deductions = parseFloat(taxForm.deductions) || 0;
    let tax = 0;
    let taxableIncome = 0;
    let rebateApplied = false;

    if (taxForm.regime === "new") {
      // New Regime: Few deductions allowed, Standard Deduction of 50k applies
      taxableIncome = Math.max(0, income - 50000); // Ignoring user deductions as most aren't valid

      if (taxableIncome <= 700000) {
        rebateApplied = true;
        tax = 0; // Section 87A Rebate
      } else {
        if (taxableIncome > 300000)
          tax += Math.min(taxableIncome - 300000, 300000) * 0.05;
        if (taxableIncome > 600000)
          tax += Math.min(taxableIncome - 600000, 300000) * 0.1;
        if (taxableIncome > 900000)
          tax += Math.min(taxableIncome - 900000, 300000) * 0.15;
        if (taxableIncome > 1200000)
          tax += Math.min(taxableIncome - 1200000, 300000) * 0.2;
        if (taxableIncome > 1500000) tax += (taxableIncome - 1500000) * 0.3;
      }
    } else {
      // Old Regime: All deductions allowed
      taxableIncome = Math.max(0, income - deductions);

      if (taxableIncome <= 500000) {
        rebateApplied = true;
        tax = 0; // Section 87A Rebate
      } else {
        if (taxableIncome > 250000)
          tax += Math.min(taxableIncome - 250000, 250000) * 0.05;
        if (taxableIncome > 500000)
          tax += Math.min(taxableIncome - 500000, 500000) * 0.2;
        if (taxableIncome > 1000000) tax += (taxableIncome - 1000000) * 0.3;
      }
    }

    // Add 4% Health & Education Cess if tax > 0
    if (tax > 0) tax = tax + tax * 0.04;

    const effectiveRate = income > 0 ? ((tax / income) * 100).toFixed(2) : 0;

    setTaxResult({
      estimated_tax: Math.round(tax),
      breakdown: {
        taxable_income: taxableIncome,
        net_income: Math.round(income - tax),
      },
      effective_rate: effectiveRate,
      rebate_applied: rebateApplied,
    });
  };

  const calculateROI = () => {
    const p = parseFloat(roiForm.investment_amount) || 0;
    const r = parseFloat(roiForm.expected_return) || 0;
    const t = parseFloat(roiForm.time_period) || 0;

    const futureValue = p * Math.pow(1 + r / 100, t);
    const profit = futureValue - p;
    const roiPercentage = p > 0 ? ((profit / p) * 100).toFixed(2) : 0;

    setRoiResult({
      roi_percentage: roiPercentage,
      profit: Math.round(profit),
      final_amount: Math.round(futureValue),
    });
  };

  return (
    <div className="min-h-screen pt-24 pb-20 bg-gray-50/50">
      <section className="py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 tracking-tight">
            Financial <span className="text-maroon-600">Calculators</span>
          </h1>
          <p className="text-lg text-gray-600">
            Advanced client-side tools for smart business decisions.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <Tabs defaultValue="tax" className="w-full">
            <TabsList className="grid w-full grid-cols-1 md:grid-cols-3 mb-8 h-auto gap-3 bg-transparent">
              <TabsTrigger
                value="tax"
                className="data-[state=active]:bg-maroon-600 data-[state=active]:text-white py-4 rounded-xl border border-gray-200 shadow-sm transition-all"
              >
                <CalcIcon className="h-5 w-5 mr-2" />
                Income Tax
              </TabsTrigger>
              <TabsTrigger
                value="roi"
                className="data-[state=active]:bg-maroon-600 data-[state=active]:text-white py-4 rounded-xl border border-gray-200 shadow-sm transition-all"
              >
                <TrendingUp className="h-5 w-5 mr-2" />
                ROI Estimator
              </TabsTrigger>
              <TabsTrigger
                value="market"
                className="data-[state=active]:bg-gray-900 data-[state=active]:text-white py-4 rounded-xl border border-gray-200 shadow-sm transition-all"
              >
                <LineChart className="h-5 w-5 mr-2" />
                Live Market
              </TabsTrigger>
            </TabsList>

            {/* --- TAX CALCULATOR --- */}
            <TabsContent
              value="tax"
              className="animate-in fade-in slide-in-from-bottom-4 duration-500"
            >
              <Card className="border-none shadow-xl rounded-3xl overflow-hidden bg-white">
                <CardContent className="p-8 md:p-12">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900">
                        Indian Tax Calculator
                      </h2>
                      <p className="text-gray-500">FY 2024-25 / AY 2025-26</p>
                    </div>
                    {/* Regime Toggle */}
                    <div className="flex bg-gray-100 p-1 rounded-lg">
                      <button
                        onClick={() =>
                          setTaxForm({ ...taxForm, regime: "new" })
                        }
                        className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${taxForm.regime === "new" ? "bg-white shadow-sm text-maroon-600" : "text-gray-500"}`}
                      >
                        New Regime
                      </button>
                      <button
                        onClick={() =>
                          setTaxForm({ ...taxForm, regime: "old" })
                        }
                        className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${taxForm.regime === "old" ? "bg-white shadow-sm text-maroon-600" : "text-gray-500"}`}
                      >
                        Old Regime
                      </button>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-12">
                    <div className="space-y-6">
                      <div>
                        <Label className="text-gray-700 font-bold">
                          Annual Income (₹)
                        </Label>
                        <Input
                          type="number"
                          placeholder="e.g., 1200000"
                          value={taxForm.income}
                          onChange={(e) =>
                            setTaxForm({ ...taxForm, income: e.target.value })
                          }
                          className="mt-2 text-lg py-6 focus:ring-maroon-600"
                        />
                      </div>
                      {taxForm.regime === "old" && (
                        <div>
                          <Label className="text-gray-700 font-bold flex items-center gap-2">
                            Total Deductions (₹){" "}
                            <Info className="h-4 w-4 text-gray-400" />
                          </Label>
                          <Input
                            type="number"
                            placeholder="80C, 80D, HRA, etc."
                            value={taxForm.deductions}
                            onChange={(e) =>
                              setTaxForm({
                                ...taxForm,
                                deductions: e.target.value,
                              })
                            }
                            className="mt-2 text-lg py-6 focus:ring-maroon-600"
                          />
                        </div>
                      )}
                      {taxForm.regime === "new" && (
                        <div className="bg-maroon-50 p-4 rounded-xl border border-maroon-100 text-maroon-800 text-sm">
                          <Info className="h-5 w-5 inline mr-2" />
                          Standard deduction of ₹50,000 is automatically
                          applied. Most other deductions are not applicable
                          under the New Regime.
                        </div>
                      )}
                      <Button
                        onClick={calculateTax}
                        disabled={!taxForm.income}
                        className="w-full bg-maroon-600 hover:bg-maroon-700 py-6 text-lg rounded-xl shadow-lg transition-all"
                      >
                        Calculate Tax
                      </Button>
                    </div>

                    <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100 flex flex-col justify-center relative overflow-hidden">
                      {taxResult ? (
                        <div className="space-y-6 relative z-10">
                          <div className="border-b border-gray-200 pb-6">
                            <p className="text-gray-500 font-bold uppercase tracking-wider mb-2">
                              Estimated Tax Payable
                            </p>
                            <p className="text-5xl font-black text-maroon-600">
                              ₹{taxResult.estimated_tax.toLocaleString("en-IN")}
                            </p>
                            {taxResult.rebate_applied && (
                              <p className="text-green-600 font-bold text-sm mt-2 flex items-center">
                                ✨ ₹0 Tax: Eligible for Full Rebate under Sec
                                87A!
                              </p>
                            )}
                          </div>
                          <div className="space-y-3">
                            <div className="flex justify-between text-gray-600">
                              <span>Net Taxable Income:</span>
                              <span className="font-bold text-gray-900">
                                ₹
                                {taxResult.breakdown.taxable_income.toLocaleString(
                                  "en-IN",
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between text-gray-600">
                              <span>Effective Tax Rate:</span>
                              <span className="font-bold text-gray-900">
                                {taxResult.effective_rate}%
                              </span>
                            </div>
                            <div className="flex justify-between items-center pt-4 mt-4 border-t border-gray-200">
                              <span className="font-bold text-gray-900">
                                In-Hand Income:
                              </span>
                              <span className="font-black text-2xl text-green-600">
                                ₹
                                {taxResult.breakdown.net_income.toLocaleString(
                                  "en-IN",
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-gray-400">
                          <CalcIcon className="h-16 w-16 mx-auto mb-4 opacity-20" />
                          <p>Enter your details to generate breakdown.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* --- ROI CALCULATOR (Unchanged layout, just styled) --- */}
            <TabsContent
              value="roi"
              className="animate-in fade-in slide-in-from-bottom-4 duration-500"
            >
              <Card className="border-none shadow-xl rounded-3xl overflow-hidden bg-white">
                <CardContent className="p-8 md:p-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-8">
                    Return on Investment Estimator
                  </h2>
                  <div className="grid md:grid-cols-2 gap-12">
                    <div className="space-y-6">
                      <div>
                        <Label className="text-gray-700 font-bold">
                          Total Investment (₹)
                        </Label>
                        <Input
                          type="number"
                          placeholder="e.g., 500000"
                          value={roiForm.investment_amount}
                          onChange={(e) =>
                            setRoiForm({
                              ...roiForm,
                              investment_amount: e.target.value,
                            })
                          }
                          className="mt-2 text-lg py-6 focus:ring-maroon-600"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-700 font-bold">
                            Annual Return (%)
                          </Label>
                          <Input
                            type="number"
                            placeholder="e.g., 12"
                            value={roiForm.expected_return}
                            onChange={(e) =>
                              setRoiForm({
                                ...roiForm,
                                expected_return: e.target.value,
                              })
                            }
                            className="mt-2 text-lg py-6 focus:ring-maroon-600"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-700 font-bold">
                            Time (Years)
                          </Label>
                          <Input
                            type="number"
                            placeholder="e.g., 5"
                            value={roiForm.time_period}
                            onChange={(e) =>
                              setRoiForm({
                                ...roiForm,
                                time_period: e.target.value,
                              })
                            }
                            className="mt-2 text-lg py-6 focus:ring-maroon-600"
                          />
                        </div>
                      </div>
                      <Button
                        onClick={calculateROI}
                        disabled={
                          !roiForm.investment_amount ||
                          !roiForm.expected_return ||
                          !roiForm.time_period
                        }
                        className="w-full bg-maroon-600 hover:bg-maroon-700 py-6 text-lg rounded-xl shadow-lg transition-all"
                      >
                        Calculate Returns
                      </Button>
                    </div>

                    <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100 flex flex-col justify-center">
                      {roiResult ? (
                        <div className="space-y-6">
                          <div className="border-b border-gray-200 pb-6">
                            <p className="text-gray-500 font-bold uppercase tracking-wider mb-2">
                              Total Maturity Value
                            </p>
                            <p className="text-5xl font-black text-maroon-600">
                              ₹{roiResult.final_amount.toLocaleString("en-IN")}
                            </p>
                          </div>
                          <div className="space-y-4">
                            <div className="flex justify-between text-gray-600">
                              <span>Invested Amount:</span>
                              <span className="font-bold text-gray-900">
                                ₹
                                {parseFloat(
                                  roiForm.investment_amount,
                                ).toLocaleString("en-IN")}
                              </span>
                            </div>
                            <div className="flex justify-between text-gray-600">
                              <span>Est. Profit Generated:</span>
                              <span className="font-bold text-green-600">
                                + ₹{roiResult.profit.toLocaleString("en-IN")}
                              </span>
                            </div>
                            <div className="flex justify-between items-center pt-4 mt-4 border-t border-gray-200">
                              <span className="font-bold text-gray-900">
                                Absolute ROI:
                              </span>
                              <span className="font-black text-2xl text-maroon-600">
                                {roiResult.roi_percentage}%
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-gray-400">
                          <TrendingUp className="h-16 w-16 mx-auto mb-4 opacity-20" />
                          <p>Enter parameters to see projections.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* --- HYBRID LIVE MARKET TAB --- */}
            <TabsContent
              value="market"
              className="animate-in fade-in slide-in-from-bottom-4 duration-500"
            >
              <Card className="border-none shadow-2xl rounded-3xl overflow-hidden">
                <CardContent className="p-0 bg-gray-900 text-white relative">
                  {/* Top Navbar for Market Tab */}
                  <div className="border-b border-gray-800 p-6 flex justify-between items-center bg-gray-950">
                    <div className="flex gap-4">
                      <button
                        onClick={() => setMarketTab("crypto")}
                        className={`px-4 py-2 font-bold rounded-lg transition-all ${marketTab === "crypto" ? "bg-maroon-600 text-white" : "text-gray-400 hover:text-white"}`}
                      >
                        Real Crypto Data
                      </button>
                      <button
                        onClick={() => setMarketTab("stocks")}
                        className={`px-4 py-2 font-bold rounded-lg transition-all ${marketTab === "stocks" ? "bg-maroon-600 text-white" : "text-gray-400 hover:text-white"}`}
                      >
                        NSE Stocks (Simulated)
                      </button>
                    </div>
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                    </span>
                  </div>

                  <div className="p-8">
                    {marketTab === "crypto" ? (
                      <div>
                        <div className="mb-6">
                          <h3 className="text-xl font-bold flex items-center gap-2">
                            <Bitcoin className="text-yellow-500" /> Live Crypto
                            Market (Real API)
                          </h3>
                          <p className="text-sm text-gray-400">
                            Live data fetched from CoinGecko API.
                          </p>
                        </div>
                        {cryptoLoading && cryptoData.length === 0 ? (
                          <div className="text-center py-12 text-gray-500 animate-pulse">
                            Fetching live blockchain data...
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {cryptoData.map((coin) => {
                              const isPositive =
                                coin.price_change_percentage_24h >= 0;
                              return (
                                <div
                                  key={coin.id}
                                  className="bg-gray-800 rounded-2xl p-6 border border-gray-700"
                                >
                                  <div className="flex justify-between items-start mb-4">
                                    <div className="flex items-center gap-3">
                                      <img
                                        src={coin.image}
                                        alt={coin.name}
                                        className="w-8 h-8 rounded-full"
                                      />
                                      <div>
                                        <h4 className="font-bold text-lg text-white uppercase">
                                          {coin.symbol}
                                        </h4>
                                        <p className="text-xs text-gray-400">
                                          {coin.name}
                                        </p>
                                      </div>
                                    </div>
                                    <div
                                      className={`p-2 rounded-lg ${isPositive ? "bg-green-500/10" : "bg-red-500/10"}`}
                                    >
                                      {isPositive ? (
                                        <TrendingUp className="h-5 w-5 text-green-400" />
                                      ) : (
                                        <TrendingDown className="h-5 w-5 text-red-400" />
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-2xl font-mono font-bold">
                                    ₹
                                    {coin.current_price.toLocaleString("en-IN")}
                                  </div>
                                  <div
                                    className={`mt-2 font-mono text-sm font-bold ${isPositive ? "text-green-400" : "text-red-400"}`}
                                  >
                                    {isPositive ? "+" : ""}
                                    {coin.price_change_percentage_24h.toFixed(
                                      2,
                                    )}
                                    % (24h)
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <div className="mb-6">
                          <h3 className="text-xl font-bold">
                            Indian Equities (NSE)
                          </h3>
                          <p className="text-sm text-gray-400">
                            Advanced simulation engine mimicking market volume.
                          </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {stocks.map((stock, idx) => {
                            const isPositive = stock.change >= 0;
                            return (
                              <div
                                key={idx}
                                className="bg-gray-800 rounded-2xl p-6 border border-gray-700"
                              >
                                <div className="flex justify-between items-start mb-4">
                                  <div>
                                    <h4 className="font-bold text-lg text-white">
                                      {stock.symbol}
                                    </h4>
                                    <p className="text-xs text-gray-400">
                                      {stock.name}
                                    </p>
                                  </div>
                                  <div
                                    className={`p-2 rounded-lg ${isPositive ? "bg-green-500/10" : "bg-red-500/10"}`}
                                  >
                                    {isPositive ? (
                                      <TrendingUp className="h-5 w-5 text-green-400" />
                                    ) : (
                                      <TrendingDown className="h-5 w-5 text-red-400" />
                                    )}
                                  </div>
                                </div>
                                <div className="text-3xl font-mono font-bold">
                                  ₹{stock.price.toFixed(2)}
                                </div>
                                <div
                                  className={`flex items-center gap-2 mt-2 font-mono text-sm ${isPositive ? "text-green-400" : "text-red-400"}`}
                                >
                                  <span>
                                    {isPositive ? "+" : ""}
                                    {stock.change.toFixed(2)}
                                  </span>
                                  <span>
                                    ({isPositive ? "+" : ""}
                                    {stock.percent.toFixed(2)}%)
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default Calculators;
